/**
 * Social sign-in wrappers for iOS/Android (Capacitor native plugins).
 *
 * Design :
 *  - On web, these functions throw : the UI must gate them with isNativePlatform().
 *  - Plugins are imported *dynamically* so the web bundle never pulls Capacitor
 *    runtime calls at module-load time (would crash on plain browsers).
 *  - After getting the provider token natively, we POST to our backend which
 *    does the real cryptographic verification. The backend answers with our
 *    standard JWT in an httpOnly cookie — same as email/password login.
 *
 * Env :
 *  - REACT_APP_BACKEND_URL      → required (used by axios elsewhere too)
 *  - REACT_APP_APPLE_CLIENT_ID  → defaults to iOS bundle id "com.unavocatenor.app"
 */
import axios from "axios";
import { isNativePlatform } from "./platform";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APPLE_CLIENT_ID =
  process.env.REACT_APP_APPLE_CLIENT_ID || "com.unavocatenor.app";

/** Thrown when the caller invoked a native-only function on web. */
export class NotNativeError extends Error {
  constructor() {
    super("Social sign-in natif indisponible hors iOS/Android");
  }
}

function _rand(len = 16) {
  // Not cryptographic-grade; just a per-login unique value for Apple nonce/state.
  const bytes = new Uint8Array(len);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Native Sign in with Apple → POST /api/auth/apple.
 * Returns the backend response { token, user } (cookie is also set).
 */
export async function signInWithApple({ referrerId } = {}) {
  if (!isNativePlatform()) throw new NotNativeError();
  const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");

  const options = {
    clientId: APPLE_CLIENT_ID,
    redirectURI: "", // not used on native iOS
    scopes: "email name",
    state: _rand(16),
    nonce: _rand(16),
  };
  const result = await SignInWithApple.authorize(options);
  // The plugin wraps response under result.response; be defensive for older versions.
  const r = result?.response || result || {};
  const identityToken = r.identityToken;
  if (!identityToken) throw new Error("Apple n'a pas retourné d'identityToken");

  const payload = {
    identityToken,
    // email/name are returned only on first-ever login; forward them so the
    // backend can persist them before Apple stops sending them.
    user: {
      email: r.email || null,
      name:
        [r.givenName, r.familyName].filter(Boolean).join(" ").trim() ||
        r.user?.name ||
        null,
    },
  };
  if (referrerId) payload.referrer_id = referrerId;

  const { data } = await axios.post(`${API}/auth/apple`, payload, {
    withCredentials: true,
  });
  return data; // { token, user }
}

/**
 * Native Google Sign-In → POST /api/auth/google-native.
 * iOS client id is configured via capacitor.config.ts (GoogleAuth plugin).
 */
export async function signInWithGoogleNative({ referrerId } = {}) {
  if (!isNativePlatform()) throw new NotNativeError();
  const mod = await import("@codetrix-studio/capacitor-google-auth");
  const GoogleAuth = mod.GoogleAuth || mod.default?.GoogleAuth || mod.default;

  try {
    // initialize() is a no-op if already initialized; call it defensively.
    if (typeof GoogleAuth.initialize === "function") GoogleAuth.initialize();
  } catch {
    /* ignore — some plugin versions auto-initialize */
  }

  const result = await GoogleAuth.signIn();
  const idToken =
    result?.authentication?.idToken ||
    result?.idToken ||
    result?.serverAuthCode; // fallback, not ideal
  if (!idToken) throw new Error("Google n'a pas retourné d'idToken");

  const payload = { idToken };
  if (referrerId) payload.referrer_id = referrerId;

  const { data } = await axios.post(`${API}/auth/google-native`, payload, {
    withCredentials: true,
  });
  return data; // { token, user }
}
